import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiArrowRight, FiCheck, FiMail, FiPackage, FiTruck } from "react-icons/fi";
import siteConfig from "../Datas/siteConfig";
import "../css/paysuccess.css";

const formatPrice = (value) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(value) || 0);

const readLastOrder = () => {
  if (typeof window === "undefined") return null;

  try {
    return JSON.parse(window.sessionStorage.getItem("lastOrder") || "null");
  } catch {
    return null;
  }
};

const validOrder = (value) =>
  value &&
  typeof value === "object" &&
  typeof value.id === "string" &&
  typeof value.email === "string" &&
  typeof value.deliveryAddress === "string" &&
  Number.isFinite(Number(value.itemCount)) &&
  Number.isFinite(Number(value.shipping)) &&
  Number.isFinite(Number(value.total));

function PaySuccess() {
  const location = useLocation();
  const order = useMemo(() => {
    const candidate = location.state?.order || readLastOrder();
    return validOrder(candidate) ? candidate : null;
  }, [location.state]);

  useEffect(() => {
    document.title = `${order ? "Order confirmed" : "Order status"} | ${siteConfig.brandName}`;
    return () => {
      document.title = `${siteConfig.brandName} | Outdoor clothing & equipment`;
    };
  }, [order]);

  if (!order) {
    return (
      <main className="order-success order-success--missing" aria-labelledby="missing-order-title">
        <section className="order-success__hero">
          <div className="order-success__check order-success__check--neutral">
            <FiPackage aria-hidden="true" />
          </div>
          <p>Order confirmation</p>
          <h1 id="missing-order-title">No recent order found.</h1>
          <span>
            This confirmation link may have expired, or no order has been placed
            in this browser session. Your basket has not been changed.
          </span>
        </section>
        <section className="order-success__actions" aria-label="Next steps">
          <Link to="/shop">Continue shopping <FiArrowRight aria-hidden="true" /></Link>
          <Link to="/contact">Contact customer care</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="order-success" aria-labelledby="order-confirmation-title">
      <section className="order-success__hero" aria-live="polite">
        <div className="order-success__check"><FiCheck aria-hidden="true" /></div>
        <p>Order confirmed</p>
        <h1 id="order-confirmation-title">Thank you{order.customerName ? `, ${order.customerName.split(" ")[0]}` : ""}.</h1>
        <span>
          Your order is in. We will send tracking details as soon as it leaves
          our warehouse.
        </span>
        <strong>Order number {order.id}</strong>
      </section>

      <section className="order-success__details" aria-labelledby="confirmation-details-title">
          <div>
            <p>Confirmation details</p>
            <h2 id="confirmation-details-title">What happens next?</h2>
          </div>
          <div className="order-success__steps">
            <article><FiMail aria-hidden="true" /><span><small>01</small><strong>Confirmation sent</strong><p>A receipt has been sent to {order.email}.</p></span></article>
            <article><FiPackage aria-hidden="true" /><span><small>02</small><strong>We prepare your gear</strong><p>Our team checks and carefully packs every item.</p></span></article>
            <article><FiTruck aria-hidden="true" /><span><small>03</small><strong>On its way</strong><p>{order.shippingMethod === "express" ? "Express delivery normally takes 1–2 working days." : "Standard delivery normally takes 3–5 working days."}</p></span></article>
          </div>
      </section>

      <section className="order-success__summary" aria-label="Order summary">
          <div><span>Items</span><strong>{order.itemCount}</strong></div>
          <div><span>Delivery</span><strong>{order.shipping === 0 ? "Free" : formatPrice(order.shipping)}</strong></div>
          <div><span>Order total</span><strong>{formatPrice(order.total)}</strong></div>
          <div><span>Delivering to</span><strong>{order.deliveryAddress}</strong></div>
      </section>

      <section className="order-success__actions">
        <Link to="/shop">Continue shopping <FiArrowRight aria-hidden="true" /></Link>
        <Link to="/contact">Need help with this order?</Link>
      </section>
    </main>
  );
}

export default PaySuccess;
